import { CalendarCheck, ClipboardCheck, BookOpen, CheckCircle2, ShieldCheck } from 'lucide-react';
import Card from '../../components/ui/Card';
import OutcomeGrid from './OutcomeGrid';
import MirrorPanel from './MirrorPanel';
import EvidenceTimeline from './EvidenceTimeline';
import StatusTag from './StatusTag';
import type { TagTone } from './StatusTag';
import type { LearnerRecordDto, AttendanceCell, LearnerStatus } from './types';

// BP 27 — the shared record view. Used by both the student's own /me/record
// (mode="student") and the instructor's /teach/students/:userId
// (mode="instructor"). Rendering is identical either way — transparency by
// default (BP 27 §Assumptions) — the instructor page just adds write
// controls (NoteComposer, outcome assessment, status change) around this.

const STATUS_LABEL: Record<LearnerStatus, string> = { active: 'Active', withdrawn: 'Withdrawn', completed: 'Completed' };
const STATUS_TONE: Record<LearnerStatus, TagTone> = { active: 'success', withdrawn: 'danger', completed: 'accent' };

const CELL_TONE: Record<AttendanceCell, string> = {
  P: 'bg-ws-success-quiet text-ws-success', M: 'bg-ws-accent-quiet text-ws-accent-ink',
  R: 'bg-ws-accent-quiet text-ws-accent-ink', A: 'bg-ws-danger/10 text-ws-danger', '—': 'bg-ws-surface-2 text-ws-ink-4',
};

function fmtDate(d: string | null | undefined): string {
  return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function Stat({ icon: Icon, label, value, sub }: { icon: typeof CalendarCheck; label: string; value: React.ReactNode; sub?: string }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ws-ink-3">
        <Icon size={13} className="text-ws-accent" />
        {label}
      </div>
      <p className="mt-1.5 text-2xl font-semibold leading-none text-ws-ink">{value}</p>
      {sub && <p className="mt-1 text-[11px] text-ws-ink-4">{sub}</p>}
    </Card>
  );
}

export default function LearnerRecordView({
  record, mode = 'student', extraActions, noteComposer, hideIdentityHeading = false,
}: {
  record: LearnerRecordDto;
  mode?: 'student' | 'instructor';
  /** Instructor-only controls (outcome assessment form, status change) — kept
   * out of this component so it stays identical for both audiences. */
  extraActions?: React.ReactNode;
  /** NoteComposer, rendered by the instructor page only. */
  noteComposer?: React.ReactNode;
  /** Suppress the name + student-number heading only; the engagement facts
   * beneath it stay. For a host that already names the student immediately
   * above this view (the instructor's portfolio page), where repeating it
   * prints the same person's name twice within 200px. Off by default, so
   * /me/record is untouched. */
  hideIdentityHeading?: boolean;
}) {
  const { student, first_hand: fh, notes } = record;

  return (
    <div className="space-y-4" data-mode={mode}>
      {/* Identity — the record's write controls live here (instructor only);
          the status itself moved into the summary row below so it reads with
          the weight of a fact, not a corner decoration. */}
      <Card className="p-4 print:shadow-none">
        {!hideIdentityHeading && (
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-ws-ink">
                {[student.given_name, student.family_name].filter(Boolean).join(' ') || student.email}
              </h2>
              <p className="text-sm text-ws-ink-3">
                {student.student_id ? `Student ID ${student.student_id}` : 'No student ID on file'}
                {student.email_is_placeholder && ' · no email on file'}
              </p>
            </div>
            {extraActions && <div className="flex items-center gap-2">{extraActions}</div>}
          </div>
        )}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-ws-ink-3 sm:grid-cols-5">
          <div><dt className="inline font-medium">Logins: </dt><dd className="inline">{fh.engagement.logins}</dd></div>
          <div><dt className="inline font-medium">First login: </dt><dd className="inline">{fmtDate(student.first_login_at)}</dd></div>
          <div><dt className="inline font-medium">Last seen: </dt><dd className="inline">{fmtDate(student.last_login_at)}</dd></div>
          <div><dt className="inline font-medium">Password rotated: </dt><dd className="inline">{fmtDate(student.password_changed_at)}</dd></div>
          <div><dt className="inline font-medium">Known devices: </dt><dd className="inline">{student.device_count}</dd></div>
        </dl>
      </Card>

      {/* Summary before detail — where you stand, at a glance. Status sits
          first among equals with the other headline numbers, sized and
          shaped the same way, so it reads as data rather than decoration. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat
          icon={ShieldCheck}
          label="Status"
          value={<StatusTag label={STATUS_LABEL[student.learner_status]} tone={STATUS_TONE[student.learner_status]} size="md" />}
          sub={student.learner_status_changed_at ? `since ${fmtDate(student.learner_status_changed_at)}` : undefined}
        />
        <Stat icon={CalendarCheck} label="Attendance" value={`${fh.attendance.percent}%`} sub={`${fh.attendance.present}/${fh.attendance.eligible} meetings`} />
        <Stat icon={ClipboardCheck} label="Participation" value={`${fh.participation.responded}/${fh.participation.expected}`} sub="activities responded" />
        <Stat icon={BookOpen} label="Lessons" value={`${fh.lessons.completed}/${fh.lessons.total}`} sub={`completed · ${fh.lessons.xp} XP`} />
        <Stat icon={CheckCircle2} label="Quiz checks" value={`${fh.quiz_checks.accuracy_percent}%`} sub={`${fh.quiz_checks.attempts} attempts`} />
      </div>

      {/* Detail — wide screens get a main column for the evidence that
          drives the summary above, and a narrower rail for reference/mirror
          data that's secondary to it. Both collapse to one column below xl,
          so this stays correct in the instructor's narrower page too. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-4">
          <Card className="overflow-hidden print:break-inside-avoid">
            <div className="border-b border-ws-border bg-ws-surface-2 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-ws-ink">Attendance — first-hand</h3>
              <p className="text-[11px] text-ws-ink-3">
                {fh.attendance.by_method.code} in-room · {fh.attendance.by_method.manual} manual ·{' '}
                {fh.attendance.by_method.remote_exception} remote-exception · {fh.attendance.voided} voided ·{' '}
                {fh.attendance.flagged} flagged
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 p-3">
              {fh.attendance.sessions.map((s) => (
                <div
                  key={s.seq}
                  title={`${s.label}${s.quality ? ` — ${s.quality}` : ''}${s.flags.length ? ` (${s.flags.join(', ')})` : ''}`}
                  className={`flex h-9 w-9 flex-col items-center justify-center rounded-md text-[11px] font-semibold ${CELL_TONE[s.cell]}`}
                >
                  <span>{s.cell}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="overflow-hidden print:break-inside-avoid">
            <div className="border-b border-ws-border bg-ws-surface-2 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-ws-ink">Class activities — first-hand</h3>
            </div>
            {fh.participation.by_session.length === 0 ? (
              <p className="px-4 py-4 text-center text-sm text-ws-ink-3">No activity-expected sessions yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ws-border text-left text-xs text-ws-ink-3">
                      <th className="px-4 py-1.5 font-medium">Meeting</th>
                      <th className="px-4 py-1.5 font-medium">Responses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fh.participation.by_session.map((s) => (
                      <tr key={s.seq} className="border-b border-ws-border last:border-0">
                        <td className="px-4 py-1.5 text-ws-ink">{s.label}</td>
                        <td className="px-4 py-1.5 text-ws-ink-2">{s.responses}/{s.steps_live || '—'} steps</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="overflow-hidden print:break-inside-avoid">
            <div className="border-b border-ws-border bg-ws-surface-2 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-ws-ink">Lessons — first-hand</h3>
              <p className="text-[11px] text-ws-ink-3">
                Every lesson opened, how far into it, and when it was last touched.
              </p>
            </div>
            {fh.lessons.rows.length === 0 ? (
              <p className="px-4 py-4 text-center text-sm text-ws-ink-3">No lesson opened yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ws-border text-left text-xs text-ws-ink-3">
                      <th className="px-4 py-1.5 font-medium">Lesson</th>
                      <th className="px-4 py-1.5 font-medium">Status</th>
                      <th className="px-4 py-1.5 font-medium">Slide reached</th>
                      <th className="px-4 py-1.5 font-medium">XP</th>
                      <th className="px-4 py-1.5 font-medium">Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fh.lessons.rows.map((r, i) => (
                      <tr key={`${r.lesson}-${i}`} className="border-b border-ws-border last:border-0">
                        <td className="px-4 py-1.5 text-ws-ink">{r.lesson}</td>
                        <td className="px-4 py-1.5">
                          <StatusTag
                            label={r.status === 'completed' ? 'Completed' : r.status === 'in_progress' ? 'In progress' : 'Started'}
                            tone={r.status === 'completed' ? 'success' : r.status === 'in_progress' ? 'accent' : 'neutral'}
                            size="sm"
                          />
                        </td>
                        <td className="px-4 py-1.5 text-ws-ink-2">{r.slide_index + 1}</td>
                        <td className="px-4 py-1.5 text-ws-ink-2">{r.xp}</td>
                        <td className="px-4 py-1.5 text-ws-ink-2">{fmtDate(r.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <OutcomeGrid outcomes={record.outcomes} />
        </div>

        <div className="min-w-0 space-y-4">
          <Card className="overflow-hidden print:break-inside-avoid">
            <div className="border-b border-ws-border bg-ws-surface-2 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-ws-ink">Surveys completed</h3>
              <p className="text-[11px] text-ws-ink-3">What was answered is never shown here — only that the survey was completed.</p>
            </div>
            <div className="flex flex-col gap-1.5 p-3">
              {([
                ['week5_checkin', 'Week-5 check-in'], ['week10_checkin', 'Week-10 check-in'],
                ['exit', 'Exit survey'], ['satisfaction', 'Satisfaction survey'],
              ] as const).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between gap-2 text-sm text-ws-ink-2">
                  <span>{label}</span>
                  <StatusTag
                    label={fh.surveys_completed[key] ? 'Completed' : 'Not yet'}
                    tone={fh.surveys_completed[key] ? 'success' : 'neutral'}
                    size="sm"
                  />
                </div>
              ))}
            </div>
          </Card>

          <MirrorPanel mirrored={record.mirrored} />
        </div>
      </div>

      {noteComposer}

      <Card className="overflow-hidden print:break-inside-avoid">
        <div className="border-b border-ws-border bg-ws-surface-2 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-ws-ink">Notes</h3>
        </div>
        {notes.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ws-ink-3">No notes yet.</p>
        ) : (
          <ul className="divide-y divide-ws-border">
            {notes.map((n) => (
              <li key={n.id} className={`px-4 py-2 text-[13px] ${n.superseded_by ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-2 text-[11px] text-ws-ink-3">
                  <span className="font-medium capitalize">{n.kind.replace(/_/g, ' ')}</span>
                  <span>·</span>
                  <span>{fmtDate(n.at)}</span>
                  <span>·</span>
                  <span>{n.author?.name || 'instructor'}</span>
                  {n.superseded_by && <span className="rounded-md bg-ws-surface-2 px-1.5 py-0.5 text-[10px]">superseded</span>}
                </div>
                <p className={`mt-0.5 text-ws-ink-2 ${n.superseded_by ? 'line-through' : ''}`}>{n.body}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <EvidenceTimeline evidence={record.evidence} />
    </div>
  );
}
